import React, { useMemo, useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../../atoms";
import { FormField } from "../FormField";
import type { Expense, Revenue } from "@/types/startup.types";
import { safeDate } from "@/utils/date";
import { alignDateToTarget, shiftDateByDays } from "@/utils/chart/bulk-date-shift.utils";

type BulkItemType = "expenses" | "revenues";
type ShiftMode = "offset" | "startup-start" | "specific-date";

export interface BulkDateShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  expenses: Expense[];
  revenues: Revenue[];
  startupStartDate: Date;
  onApplyExpenseUpdates: (
    updates: Array<{ id: string; startDate?: Date; endDate?: Date }>,
  ) => Promise<void>;
  onApplyRevenueUpdates: (
    updates: Array<{ id: string; date?: Date; startDate?: Date; endDate?: Date }>,
  ) => Promise<void>;
}

export const BulkDateShiftModal: React.FC<BulkDateShiftModalProps> = ({
  isOpen,
  onClose,
  expenses,
  revenues,
  startupStartDate,
  onApplyExpenseUpdates,
  onApplyRevenueUpdates,
}) => {
  const [itemType, setItemType] = useState<BulkItemType>("expenses");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [shiftMode, setShiftMode] = useState<ShiftMode>("offset");
  const [dayOffset, setDayOffset] = useState("0");
  const [specificDate, setSpecificDate] = useState(
    startupStartDate.toISOString().slice(0, 10),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = itemType === "expenses" ? expenses : revenues;

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(items.map((item) => item.id)));
  };

  const handleApply = async () => {
    setError(null);

    if (selectedIds.size === 0) {
      setError("Select at least one item to update.");
      return;
    }

    setIsSaving(true);
    try {
      if (itemType === "expenses") {
        const updates = expenses
          .filter((expense) => selectedIds.has(expense.id))
          .map((expense) => {
            const start = safeDate(expense.startDate) ?? startupStartDate;
            const end = safeDate(expense.endDate);

            let nextStart = start;
            let nextEnd: Date | undefined = end;

            if (shiftMode === "offset") {
              const days = Number(dayOffset) || 0;
              nextStart = shiftDateByDays(start, days) ?? start;
              nextEnd = end ? shiftDateByDays(end, days) : undefined;
            } else if (shiftMode === "startup-start") {
              nextStart = alignDateToTarget(start, startupStartDate);
              nextEnd = end ? alignDateToTarget(end, startupStartDate) : undefined;
            } else {
              const target = new Date(`${specificDate}T12:00:00`);
              nextStart = alignDateToTarget(start, target);
              nextEnd = end ? alignDateToTarget(end, target) : undefined;
            }

            return {
              id: expense.id,
              startDate: nextStart,
              endDate: nextEnd,
            };
          });

        await onApplyExpenseUpdates(updates);
      } else {
        const updates = revenues
          .filter((revenue) => selectedIds.has(revenue.id))
          .map((revenue) => {
            const primary = safeDate(revenue.date) ?? safeDate(revenue.startDate) ?? startupStartDate;
            const start = safeDate(revenue.startDate) ?? primary;
            const end = safeDate(revenue.endDate);

            let nextPrimary = primary;
            let nextStart = start;
            let nextEnd: Date | undefined = end;

            if (shiftMode === "offset") {
              const days = Number(dayOffset) || 0;
              nextPrimary = shiftDateByDays(primary, days) ?? primary;
              nextStart = shiftDateByDays(start, days) ?? start;
              nextEnd = end ? shiftDateByDays(end, days) : undefined;
            } else if (shiftMode === "startup-start") {
              nextPrimary = alignDateToTarget(primary, startupStartDate);
              nextStart = alignDateToTarget(start, startupStartDate);
              nextEnd = end ? alignDateToTarget(end, startupStartDate) : undefined;
            } else {
              const target = new Date(`${specificDate}T12:00:00`);
              nextPrimary = alignDateToTarget(primary, target);
              nextStart = alignDateToTarget(start, target);
              nextEnd = end ? alignDateToTarget(end, target) : undefined;
            }

            return {
              id: revenue.id,
              date: nextPrimary,
              startDate: nextStart,
              endDate: nextEnd,
            };
          });

        await onApplyRevenueUpdates(updates);
      }

      onClose();
      setSelectedIds(new Set());
    } catch (applyError) {
      setError(
        applyError instanceof Error
          ? applyError.message
          : "Could not update selected items.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const previewLabel = useMemo(() => {
    if (shiftMode === "offset") {
      const days = Number(dayOffset) || 0;
      return days === 0
        ? "No date change"
        : `Shift selected dates by ${days} day${days === 1 ? "" : "s"}`;
    }
    if (shiftMode === "startup-start") {
      return `Align selected start dates to ${startupStartDate.toLocaleDateString()}`;
    }
    return `Move selected start dates to ${new Date(`${specificDate}T12:00:00`).toLocaleDateString()}`;
  }, [shiftMode, dayOffset, specificDate, startupStartDate]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bulk date shift">
      <div className="space-y-5">
        <p className="text-sm text-white/65">
          Move many expenses or revenues at once — useful when retiming your plan to a new start date.
        </p>

        <div className="flex gap-2">
          {(["expenses", "revenues"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                setItemType(type);
                setSelectedIds(new Set());
              }}
              className={`rounded-[12px] px-3 py-2 text-sm font-semibold capitalize ${
                itemType === type
                  ? "bg-brand/15 text-brand ring-1 ring-brand/30"
                  : "bg-white/5 text-white/65 hover:bg-white/10"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="max-h-48 space-y-2 overflow-y-auto rounded-[14px] border border-white/10 p-3">
          <label className="flex items-center gap-2 text-sm text-white/75">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            Select all ({items.length})
          </label>
          {items.map((item) => (
            <label
              key={item.id}
              className="flex items-center gap-2 rounded-[10px] px-2 py-1.5 text-sm text-white/75 hover:bg-white/5"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(item.id)}
                onChange={(event) => {
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (event.target.checked) {
                      next.add(item.id);
                    } else {
                      next.delete(item.id);
                    }
                    return next;
                  });
                }}
              />
              <span className="truncate">{item.name}</span>
            </label>
          ))}
          {items.length === 0 ? (
            <p className="text-sm text-white/45">No {itemType} to update yet.</p>
          ) : null}
        </div>

        <div className="space-y-3 rounded-[14px] border border-white/10 bg-surface/20 p-4">
          <p className="text-sm font-semibold text-white">How should dates move?</p>
          <label className="flex items-center gap-2 text-sm text-white/75">
            <input
              type="radio"
              checked={shiftMode === "offset"}
              onChange={() => setShiftMode("offset")}
            />
            Shift by days
          </label>
          {shiftMode === "offset" ? (
            <FormField label="Days (+ forward, − backward)">
              <input
                type="number"
                value={dayOffset}
                onChange={(event) => setDayOffset(event.target.value)}
                className="sh-input"
              />
            </FormField>
          ) : null}

          <label className="flex items-center gap-2 text-sm text-white/75">
            <input
              type="radio"
              checked={shiftMode === "startup-start"}
              onChange={() => setShiftMode("startup-start")}
            />
            Move to startup start date
          </label>

          <label className="flex items-center gap-2 text-sm text-white/75">
            <input
              type="radio"
              checked={shiftMode === "specific-date"}
              onChange={() => setShiftMode("specific-date")}
            />
            Move to a specific date
          </label>
          {shiftMode === "specific-date" ? (
            <FormField label="Target date">
              <input
                type="date"
                value={specificDate}
                onChange={(event) => setSpecificDate(event.target.value)}
                className="sh-input"
              />
            </FormField>
          ) : null}

          <p className="text-xs text-white/45">{previewLabel}</p>
        </div>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="md" onClick={handleApply} disabled={isSaving}>
            {isSaving ? "Updating..." : "Apply changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
